import _ from 'lodash';

import { getPrettyPriceDisplay } from './formatting';
import { formatNumber, formatPercent } from './url_data_agent_evaluations_helpers';

export const READINESS_WINDOW_OPTIONS = [
  { value: 1, label: '24h' },
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' }
];
export const DEFAULT_READINESS_WINDOW = 1;

// Summarize links started in the selected period using the latest recorded review outcomes.
export const getReadinessReport = (readiness, days) => {
  const t = getTallies(readiness);
  return {
    steps: getSteps(readiness, t, days),
    funnel: getFunnel(readiness),
    creationRequestsNote: getCreationRequestsNote(readiness),
    stats: [
      getReviewStat(
        'matches',
        'Match review agreement',
        t.matchesRight,
        t.matchesWrong,
        'Reviewers kept the suggested product.',
        'Automatic matches are excluded.'
      ),
      getReviewStat(
        'new-product',
        'New product confirmed',
        t.newProduct.humanCreated,
        t.newProduct.humanMatchedExisting + t.newProduct.humanDismissed,
        'The catalog team added a new product.',
        'APM suggested that no existing product matched.'
      ),
      getReviewStat(
        'dismiss',
        'Dismissal review agreement',
        t.dismiss.humanDismissed,
        t.dismiss.humanMatchedExisting + t.dismiss.humanCreated,
        'The catalog team dismissed the link.',
        'A dismissal can also mean missing page evidence; it does not confirm a non-product.'
      )
    ],
    footer: getFooter(readiness)
  };
};

const MIN_DECIDED = 10;
const rate = (numerator, denominator) => (denominator ? numerator / denominator : null);
const wholePercent = value => (value == null ? '—' : `${Math.round(value * 100)}%`);
const formatSeconds = seconds => (seconds == null ? '—' : seconds < 60 ? `${Math.round(seconds)}s` : `${_.round(seconds / 60, 1)}m`);

const getTallies = ({ bands, coverage, released, expired, paused }) => {
  const sum = key => _.sumBy(bands, key);
  const unreviewed = [released.byVerdict.sibling, expired, paused].filter(Boolean);
  return {
    routed: coverage.routed,
    newProduct: released.byVerdict.new_product,
    dismiss: released.byVerdict.dismiss,
    matchesRight: sum('approved') + _.sumBy(unreviewed, 'humanMatchedSame'),
    matchesWrong:
      sum('sentBack') +
      sum('corrected') +
      _.sumBy(unreviewed, 'humanMatchedOther') +
      _.sumBy(unreviewed, 'humanCreated') +
      _.sumBy(unreviewed, 'humanDismissed')
  };
};

const getSteps = ({ sample }, { routed }, days) => [
  { n: 1, title: 'A sample goes to APM', line: `Current setting: ${sample.percent}% of eligible links. The rest stay with the catalog team.` },
  {
    n: 2,
    title: 'APM searches the catalog',
    line: `${formatNumber(routed)} links sent for checking in the last ${
      days === 1 ? '24 hours' : `${days} days`
    }. It compares the retailer page with existing products.`
  },
  {
    n: 3,
    title: 'Complete proposals can apply automatically',
    line: 'Existing matches and complete new products can apply when auto-apply is enabled and confidence and evidence checks pass.'
  },
  {
    n: 4,
    title: 'Everything else goes to the catalog team',
    line:
      'Links APM will not apply automatically go straight to the catalog team with the reason, as do proposals the auto-applier refuses and links released when APM is paused.'
  }
];

const getFunnel = ({ bands, coverage, released, expired, paused }) => {
  const pending = coverage.pendingReviewByVerdict || {};
  const outcome = (key, label, count, tone) => ({ key, depth: 1, label, count, tone, share: rate(count, coverage.routed) });
  return _.compact([
    { key: 'routed', depth: 0, label: 'Links sent to APM', count: coverage.routed },
    outcome('auto', 'Completed automatically', coverage.autoApplied, 'good'),
    outcome('approved', 'Approved by your team', coverage.reviewerApproved, 'good'),
    coverage.corrected > 0 && outcome('corrected', 'Product chosen by your team', coverage.corrected, 'good'),
    outcome('open', 'Waiting to apply', coverage.pendingReview),
    ...Object.entries({
      sibling: 'Existing-product proposal',
      new_product: 'New product draft',
      ambiguous: 'Choose a catalog product',
      dismiss: 'Product status or page evidence needs review',
      blocked: 'Page evidence unavailable',
      failed: 'APM check incomplete'
    })
      .filter(([key]) => pending[key] > 0)
      .map(([key, label]) => ({ key: `pending_${key}`, depth: 2, label, count: pending[key] })),
    {
      ...outcome('released', 'Sent to catalog team', coverage.sentToHumans),
      outcomes: Object.entries({ expired: 'review expired', paused: 'APM paused', reviewer: 'sent by reviewer', other: 'other handoff' })
        .filter(([key]) => coverage.sentToHumansByReason?.[key] > 0)
        .map(([key, label]) => ({ key, label, count: coverage.sentToHumansByReason[key] }))
    },
    ...Object.entries({
      sibling: 'Existing-product match needs review',
      new_product: 'Possible new product',
      ambiguous: 'No confident match',
      dismiss: 'Product status or page evidence needs review',
      blocked: 'Page could not be checked',
      failed: 'APM check failed'
    })
      .filter(([key]) => released.byVerdict[key]?.total > 0)
      .map(([key, label]) => handOff(key, label, released.byVerdict[key])),
    expired?.total > 0 && handOff('expired', 'Match proposals whose review time ran out', expired),
    paused?.total > 0 && handOff('paused', 'Match proposals released by pause', paused),
    _.sumBy(bands, 'sentBack') > 0 && {
      key: 'sent_back',
      depth: 2,
      label: 'Match proposals sent back by reviewers',
      count: _.sumBy(bands, 'sentBack')
    },
    coverage.alreadyResolved > 0 && outcome('already-resolved', 'Already linked or processed', coverage.alreadyResolved),
    coverage.running > 0 && outcome('running', 'Still processing', coverage.running)
  ]);
};

const getCreationRequestsNote = ({ creationRequests }) =>
  creationRequests?.links > 0
    ? `Create requests are left out of these counts: ${formatNumber(creationRequests.links)} links, ${getPrettyPriceDisplay(creationRequests.costUsd, { precision: 2 })} recorded spend.`
    : null;

const handOff = (key, label, bucket) => ({
  key,
  depth: 2,
  label,
  count: bucket.total,
  outcomes: Object.entries({
    humanMatchedSame: 'same catalog product',
    humanMatchedOther: 'different catalog product',
    humanMatchedExisting: 'matched existing',
    humanCreated: 'created by team',
    humanDismissed: 'dismissed',
    undecided: 'awaiting team',
    shortlistHit: 'human’s choice was in APM shortlist'
  })
    .filter(([outcome]) => bucket[outcome] > 0)
    .map(([key, label]) => ({ key, label, count: bucket[key] }))
});

const getReviewStat = (key, label, agreed, disagreed, description, note) => {
  const checked = agreed + disagreed;
  return {
    key,
    label,
    big: checked < MIN_DECIDED ? '—' : wholePercent(rate(agreed, checked)),
    sub: `${formatNumber(agreed)} of ${formatNumber(checked)} confirmed`,
    note: checked < MIN_DECIDED ? `Waiting for more reviews (${formatNumber(checked)} of ${MIN_DECIDED}).` : description,
    detail: note
  };
};

const getFooter = ({ pipeline }) =>
  `Recorded model spend ${pipeline.totalCostUsd == null ? '—' : `$${Number(pipeline.totalCostUsd).toFixed(2)}`} · ${
    pipeline.costMissingCount == null ? 'Cost coverage unavailable' : `${formatNumber(pipeline.costMissingCount)} checks missing cost`
  } · Average cost ${pipeline.avgCostUsd == null ? '—' : `$${Number(pipeline.avgCostUsd).toFixed(3)}`} per check · ${formatPercent(
    pipeline.failureRate
  )} with execution errors in their latest check${pipeline.executionRetryCount == null || pipeline.executionExhaustedCount == null ? ''
    : ` (${formatNumber(pipeline.executionRetryCount)} retries pending or running, ${formatNumber(pipeline.executionExhaustedCount)} exhausted retries)`} · ${formatSeconds(pipeline.p50Seconds)} median processing time`;
